import {
  all,
  call,
  delay,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';

import { CHECKMARK_TIME_UNTIL_TRANSITION } from '@dmi/shared/utils/constants';
import {
  del,
  get,
  post,
  putRequest,
} from '@dmi/shared/utils/request';

import { camelizeKeys } from '../../../utils/globalHelpers';
import { setHelmetTitleProps } from '../../Main/actions';
import makeSelectMain from '../../Main/selectors';
import makeSelectMobile from '../../Mobile/selectors';
import {
  selectCurrentPaymentAlertSetting,
  selectUpdateAlertSettingsPayload,
} from '../../Settings/Communications/selectors';
import { setExistingInfo, updateFormErrors } from '../Sources/actions';
import {
  autopayEnableFailure,
  autopayEnableSuccess,
  autopayEnableValidationFailure,
  cancelFailure,
  cancelSuccess,
  changeFailure,
  changeStep,
  changeSuccess,
  changeValidationFailure,
  fetchStatusFailure,
  fetchStatusSuccess,
  setIsModalOpen,
  stopFailure,
  stopSuccess,
  updateEmailAlertSettingFailure,
} from './actions';
import {
  AUTOPAY_ENABLE,
  CANCEL,
  CHANGE,
  DEFAULT_AUTOPAY_ENABLE_ERRORS,
  FETCH_STATUS,
  FULL_ERROR_REASON_CODES,
  REASON_CODE_DICTIONARY,
  STOP,
} from './constants';
import {
  checkFullErrorReasonCodes,
  getAutopayExistingAccountInfo,
  parseReasonCodes,
} from './helpers';
import { selectConfirmationData, selectForm } from './selectors';

export function* cancelSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload: { navigationType, needsNavigation } }) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/payments/autopay/${loanNumber}/pending`;
    const { cancelConfirmationNumber, error } = yield call(del, endpoint);
    if (error) {
      // The only possible partialError is 9004.1004, which we can treat as a full error
      throw new Error('serviceError');
    } else {
      yield put(cancelSuccess(cancelConfirmationNumber));
      if (!isMobile) {
        /**
         * Setting the helmet title props here to fix the issue where the helmet title is
         * updated *after* the screenreader has started reading the page in the Autopay
         * cancellation confirmation flow.
         * TODO: Ideally we want this to be handled in the useEffect like for the other steps,
         * which would require a larger refactor.
         */
        yield put(setHelmetTitleProps({
          componentFlow: 'cancel',
          flowStep: 1,
          pathname: '/payments/autopay',
        }));
      }
      yield delay(CHECKMARK_TIME_UNTIL_TRANSITION);
    }
  } catch {
    yield put(cancelFailure());
    if (!isMobile) {
      yield put(setHelmetTitleProps({
        componentFlow: 'error',
        flowStep: 1,
        pathname: '/payments/autopay',
      }));
    }
  }
  if (isMobile) {
    const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
      navigationReplaceHandler :
      navigationHandler;
    properNavigationHandler('CancelOrStopConfirmation');
  } else if (needsNavigation) {
    navigationHandler('/payments/autopay');
  }
  yield put(setIsModalOpen({ key: 'cancel', value: false }));
}

export function* changeSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload: { navigationType, requestBody } }) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/payments/autopay/${loanNumber}`;
    const { confirmationNumber, error } = yield call(putRequest, endpoint, requestBody);
    if (error) {
      const { reasons } = error;
      if (reasons.length && !checkFullErrorReasonCodes(reasons, FULL_ERROR_REASON_CODES)) {
        const { autopay, sources } = parseReasonCodes(reasons, DEFAULT_AUTOPAY_ENABLE_ERRORS);
        yield put(changeValidationFailure(autopay));
        yield put(updateFormErrors(sources));
        const { screen } = REASON_CODE_DICTIONARY[reasons[0]];
        if (isMobile) {
          navigationHandler('AutopaySetupOverview');
          if (screen) {
            navigationHandler(screen);
          }
        } else {
          yield put(changeStep(1));
        }
      } else {
        throw new Error('serviceError');
      }
    } else {
      /*
        check if user changed their email alerts setting via the review screen
        checkbox, if so hit the api to update setting
      */
      const { receivesPaymentEmails } = yield select(selectForm('alerts'));
      const { isEmailEnabled } = yield select(selectCurrentPaymentAlertSetting());
      if (receivesPaymentEmails !== isEmailEnabled) {
        yield call(updateEmailAlertSettingSaga, baseUrl);
      }
      yield put(changeSuccess(confirmationNumber));
      if (isMobile) {
        const confirmationData = yield select(selectConfirmationData());
        /*
          pass update confirmationData as navigation params to prevent
          displaying stale data in Confirmation screen
        */
        const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
          navigationReplaceHandler :
          navigationHandler;
        properNavigationHandler('AutopayConfirmation', { confirmationData });
      } else {
        yield put(changeStep(4));
      }
    }
  } catch {
    yield put(changeFailure());
    if (isMobile) {
      const confirmationData = yield select(selectConfirmationData());
      const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('AutopayConfirmation', { confirmationData });
    } else {
      yield put(changeStep(4));
    }
  }
  yield put(setIsModalOpen({ key: 'change', value: false }));
}

export function* enableSaga(
  { baseUrl, navigationHandler, navigationReplaceHandler },
  { payload },
) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const { body, loanNumber } = payload;
    const endpoint = `${baseUrl}/payments/autopay/${loanNumber}`;
    const { confirmationNumber, error } = yield call(post, endpoint, body);
    if (error) {
      const { reasons } = error;
      if (reasons.length && !checkFullErrorReasonCodes(reasons, FULL_ERROR_REASON_CODES)) {
        const { autopay, sources } = parseReasonCodes(reasons, DEFAULT_AUTOPAY_ENABLE_ERRORS);
        yield put(autopayEnableValidationFailure(autopay));
        yield put(updateFormErrors(sources));
        const { screen } = REASON_CODE_DICTIONARY[reasons[0]];
        if (isMobile) {
          navigationHandler('AutopaySetupOverview');
          if (screen) {
            navigationHandler(screen);
          }
        } else {
          yield put(changeStep(1));
        }
      } else {
        throw new Error('serviceError');
      }
    } else {
      /*
        check if user changed their email alerts setting via the review screen
        checkbox, if so hit the api to update setting
      */
      const { receivesPaymentEmails } = yield select(selectForm('alerts'));
      const { isEmailEnabled } = yield select(selectCurrentPaymentAlertSetting());
      if (receivesPaymentEmails !== isEmailEnabled) {
        yield call(updateEmailAlertSettingSaga, baseUrl);
      }
      const { additionalPrincipal, beginDate, delayDays } = camelizeKeys(body);
      yield put(autopayEnableSuccess({
        additionalPrincipal,
        beginDate,
        confirmationNumber,
        delayDays,
      }));
      if (isMobile) {
        const confirmationData = yield select(selectConfirmationData());
        /*
          pass update confirmationData as navigation params to prevent
          displaying stale data in Confirmation screen
        */
        const properNavigationHandler = isNativeMobile ?
          navigationReplaceHandler :
          navigationHandler;
        properNavigationHandler('AutopayConfirmation', { confirmationData });
      } else {
        yield put(changeStep(4));
      }
    }
  } catch {
    yield put(autopayEnableFailure());
    if (isMobile) {
      const confirmationData = yield select(selectConfirmationData());
      const properNavigationHandler = isNativeMobile ? navigationReplaceHandler : navigationHandler;
      properNavigationHandler('AutopayConfirmation', { confirmationData });
    } else {
      yield put(changeStep(4));
    }
  }
}

export function* fetchStatusSaga({ baseUrl }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/payments/autopay/${loanNumber}`;
    const { statusData } = yield call(get, endpoint);
    const { reasons } = statusData || [];
    if (reasons) {
      statusData.isLoanMancodeY = reasons.map(({ reasonCode }) => reasonCode).includes('9001.1019');
    } else {
      statusData.isLoanMancodeY = false;
    }
    yield put(fetchStatusSuccess(statusData));
    const { canUseExistingAccountInformation } = statusData.allowedSetting || {};
    if (canUseExistingAccountInformation) {
      const existingAccountInfo = getAutopayExistingAccountInfo(statusData.currentSetting);
      yield put(setExistingInfo(existingAccountInfo, 'autopay'));
    }
  } catch {
    yield put(fetchStatusFailure({ error: true }));
  }
}

export function* stopSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload: { navigationType } }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/payments/autopay/${loanNumber}`;
    const { confirmationNumber, error } = yield call(del, endpoint);
    if (error) {
      yield put(stopFailure());
    } else {
      yield put(stopSuccess(confirmationNumber));
    }
  } catch {
    yield put(stopFailure());
  }

  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  if (isMobile) {
    const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
      navigationReplaceHandler :
      navigationHandler;
    properNavigationHandler('CancelOrStopConfirmation');
  }
  yield put(setIsModalOpen({ key: 'stop', value: false }));
}

function* updateEmailAlertSettingSaga(baseUrl) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/settings/${loanNumber}/alerts`;
    const payload = yield select(selectUpdateAlertSettingsPayload());
    const { error } = yield call(putRequest, endpoint, payload);
    if (error) {
      yield put(updateEmailAlertSettingFailure());
    }
  } catch {
    yield put(updateEmailAlertSettingFailure());
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLeading(AUTOPAY_ENABLE, enableSaga, options),
    takeLatest(CANCEL, cancelSaga, options),
    takeLeading(CHANGE, changeSaga, options),
    takeLatest(FETCH_STATUS, fetchStatusSaga, options),
    takeLatest(STOP, stopSaga, options),
  ]);
};
